import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    getTiktokUserImages,
    getInstagramUserImages,
    microwave,
    microwaveQueries,
    TiktokUserImage,
    InstagramUserImage,
    youtube,
} from '@round/api';
import { useInfiniteQuery } from '@tanstack/react-query';
import debounce from 'lodash/debounce';
import { NamedProps, OptionsType, ValueType } from 'react-select';
import useAbortableEffect from 'Hooks/useAbortableEffect';
import useMicrowaveInfluencerAccount from './hooks/queries/useMicrowaveInfluencerAccount';

export type MicrowaveInfluencerAccountOption = {
    value: number;
    label: string;
    platform: microwave.Platform;
    accountId: number;
    accountHandle: string | null;
    nickname: string | null;
    imageUrl?: string;
    influencerId: number;
};

type Return = Pick<
    NamedProps<MicrowaveInfluencerAccountOption, false>,
    'filterOption' | 'onMenuScrollToBottom' | 'inputValue' | 'onInputChange' | 'isLoading' | 'value' | 'onChange'
> & {
    options: OptionsType<MicrowaveInfluencerAccountOption>;
};

const defaultPageSize = 25;

const getLabel = (account: microwave.MicrowaveInfluencerAccount): string =>
    account.account_handle ?? account.nickname ?? `Account #${account.id}`;

const mapAccountToOption = (
    account: microwave.MicrowaveInfluencerAccount,
    imageUrl: string | undefined
): MicrowaveInfluencerAccountOption => ({
    value: account.id,
    label: getLabel(account),
    platform: account.platform,
    accountId: account.account_id,
    accountHandle: account.account_handle,
    nickname: account.nickname,
    imageUrl,
    influencerId: account.influencer_id,
});

export default function useMicrowaveInfluencerAccountsSelect(initialAccountId?: number): Return {
    const [inputValue, setInputValue] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [value, setValue] = useState<ValueType<MicrowaveInfluencerAccountOption, false>>(null);

    const debouncedSetSearch = useMemo(() => debounce((next: string) => setDebouncedSearch(next), 250), []);
    useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);

    const infiniteQuery = useInfiniteQuery({
        ...microwaveQueries.influencerAccounts.options.infinite({
            search: debouncedSearch || undefined,
            page_size: defaultPageSize,
        }),
    });

    const pagedAccounts = useMemo(
        () => infiniteQuery.data?.pages.flatMap((p) => p.results) ?? [],
        [infiniteQuery.data]
    );

    const shouldFetchInitial =
        typeof initialAccountId === 'number' && !pagedAccounts.find((a) => a.id === initialAccountId);

    const { data: initialAccount } = useMicrowaveInfluencerAccount(initialAccountId, { enabled: shouldFetchInitial });

    // Combine paged accounts with initial account (if not already in the paged set)
    const accounts = useMemo(() => {
        if (initialAccount && !pagedAccounts.find((a) => a.id === initialAccount.id)) {
            return [initialAccount, ...pagedAccounts];
        }
        return pagedAccounts;
    }, [initialAccount, pagedAccounts]);

    const getImageUrl = useMicrowaveInfluencerAccountImages(accounts);

    const options = useMemo(
        () => accounts.map((account) => mapAccountToOption(account, getImageUrl(account))),
        [accounts, getImageUrl]
    );

    // Re-hydrate selected value from initial account id
    useEffect(() => {
        if (typeof initialAccountId !== 'number') {
            return;
        }
        if (value?.value === initialAccountId) {
            return;
        }
        const account = accounts.find((a) => a.id === initialAccountId);
        if (account) {
            setValue(mapAccountToOption(account, getImageUrl(account)));
        }
    }, [initialAccountId, accounts, getImageUrl, value?.value]);

    return {
        value,
        onChange: (next) => {
            setValue(next);
        },
        options,
        filterOption: null,
        inputValue,
        onInputChange: (next: string) => {
            setInputValue(next);
            debouncedSetSearch(next);
        },
        onMenuScrollToBottom: () => {
            if (infiniteQuery.hasNextPage && !infiniteQuery.isFetchingNextPage) {
                void infiniteQuery.fetchNextPage();
            }
        },
        isLoading: infiniteQuery.isFetching && !infiniteQuery.data,
    };
}

// TODO ROU-376: replace with useTiktokUserImageQueries / useInstagramUserImageQueries / useYoutubeChannelQueries once built
function useMicrowaveInfluencerAccountImages(accounts: microwave.MicrowaveInfluencerAccount[]) {
    const [tiktokImages, setTiktokImages] = useState<TiktokUserImage[]>([]);
    const [instagramImages, setInstagramImages] = useState<InstagramUserImage[]>([]);
    const [youtubeChannels, setYoutubeChannels] = useState<youtube.Channel[]>([]);

    useAbortableEffect(
        (signal) => {
            const tiktokIdsToFetch = accounts
                .filter((a) => a.platform === 'tiktok')
                .map((a) => a.account_id)
                .filter((id) => !tiktokImages.find((img) => img.user_id === id));
            const instagramIdsToFetch = accounts
                .filter((a) => a.platform === 'instagram')
                .map((a) => a.account_id)
                .filter((id) => !instagramImages.find((img) => img.user_id === id));
            const youtubeIdsToFetch = accounts
                .filter((a) => a.platform === 'youtube')
                .map((a) => a.account_id)
                .filter((id) => !youtubeChannels.find((channel) => channel.id === id));

            if (!tiktokIdsToFetch.length && !instagramIdsToFetch.length && !youtubeIdsToFetch.length) {
                return;
            }

            void Promise.allSettled([
                tiktokIdsToFetch.length
                    ? getTiktokUserImages(tiktokIdsToFetch, { signal }).then((images) =>
                          setTiktokImages((prev) => prev.concat(images))
                      )
                    : Promise.resolve(),
                instagramIdsToFetch.length
                    ? getInstagramUserImages(instagramIdsToFetch, { signal }).then((res) =>
                          setInstagramImages((prev) => prev.concat(res.data))
                      )
                    : Promise.resolve(),
                youtubeIdsToFetch.length
                    ? youtube
                          .getChannels({ id: youtubeIdsToFetch.join(','), page_size: youtubeIdsToFetch.length })
                          .then((res) =>
                              res.status === 200
                                  ? setYoutubeChannels((prev) => prev.concat(res.data.results))
                                  : Promise.resolve()
                          )
                    : Promise.resolve(),
            ]);
        },
        [accounts, tiktokImages, instagramImages, youtubeChannels]
    );

    return useCallback(
        (account: microwave.MicrowaveInfluencerAccount): string | undefined => {
            if (account.platform === 'tiktok') {
                const image = tiktokImages.find((img) => img.user_id === account.account_id);
                return image?.avatar_thumb.cached_url || image?.avatar_thumb.original_url;
            }
            if (account.platform === 'instagram') {
                const image = instagramImages.find((img) => img.user_id === account.account_id);
                return image?.avatar_thumb.cached_url || image?.avatar_thumb.original_url;
            }
            if (account.platform === 'youtube') {
                return youtubeChannels.find((channel) => channel.id === account.account_id)?.thumbnail || undefined;
            }
            return undefined;
        },
        [tiktokImages, instagramImages, youtubeChannels]
    );
}
